import { useReveal } from "../lib/useReveal";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import HopeNamingNote from "./HopeNamingNote";

const PLANS = [
    {
        name: "Genesis Free",
        price: "$0",
        note: "A gentle place to start",
        description:
            "Private daily check-ins, secure journaling, supportive conversations with Hope, and access to the treatment directory.",
        features: [
            "Talk to Hope anytime",
            "Daily check-ins and private journaling",
            "Privacy-first recovery support",
            "Browse verified directory listings",
        ],
        featured: false,
        cta: "Join Waitlist",
    },
    {
        name: "Hope Plus",
        price: "Soon",
        note: "Premium tier for the Hope AI companion",
        description:
            "Expanded companion features for Hope, deeper recovery insights, and additional guided support tools as the product grows.",
        features: [
            "Enhanced guidance and reflections",
            "Expanded milestone and trend tracking",
            "More premium journal prompts",
            "Priority access to new features",
        ],
        featured: true,
        cta: "Join the Waitlist",
    },
    {
        name: "Programs & Teams",
        price: "Custom",
        note: "For sober living homes and treatment organizations",
        description:
            "A future option for providers who want a polished, privacy-respecting digital companion alongside their real-world care.",
        features: [
            "Directory and referral visibility",
            "Launch support for program partners",
            "Custom onboarding options",
            "Contact for early access",
        ],
        featured: false,
        cta: "Contact Sales",
    },
];

export default function Pricing() {
    const ref = useReveal();

    return (
        <section
            id="pricing"
            ref={ref}
            data-testid="pricing-section"
            className="py-20 md:py-24 bg-background scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14 reveal">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                        Pricing
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                        Simple pricing that keeps the door open.
                    </h2>
                    <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                        Free to start, free to stay. Premium features are coming, but the core — Hope,
                        journaling, daily check-ins, the directory — will always be free.
                    </p>
                    <HopeNamingNote className="mt-4 text-muted-foreground" />
                </div>

                <div className="grid lg:grid-cols-3 gap-6 reveal">
                    {PLANS.map((plan, index) => (
                        <article
                            key={plan.name}
                            data-testid={`pricing-card-${index}`}
                            className={`rounded-[2rem] border p-8 flex flex-col ${
                                plan.featured
                                    ? "bg-slate-950 text-white border-slate-950 shadow-2xl shadow-slate-900/10 -translate-y-1"
                                    : "bg-card text-foreground"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div
                                        className={`text-xs uppercase tracking-[0.18em] font-semibold ${
                                            plan.featured ? "text-emerald-300" : "text-muted-foreground"
                                        }`}
                                    >
                                        {plan.note}
                                    </div>
                                    <h3 className="text-2xl font-bold tracking-tight mt-3">{plan.name}</h3>
                                </div>
                                {plan.featured && (
                                    <div className="px-3 py-1 rounded-full bg-white/10 text-xs font-semibold">
                                        Most anticipated
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 flex items-end gap-2">
                                <span className="text-5xl font-bold tracking-tight">{plan.price}</span>
                                <span
                                    className={`pb-1 text-sm ${
                                        plan.featured ? "text-white/60" : "text-muted-foreground"
                                    }`}
                                >
                                    {plan.price === "$0" ? "/ forever" : ""}
                                </span>
                            </div>

                            <p
                                className={`mt-5 leading-relaxed ${
                                    plan.featured ? "text-white/72" : "text-muted-foreground"
                                }`}
                            >
                                {plan.description}
                            </p>

                            <ul className="mt-8 space-y-3 flex-1">
                                {plan.features.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3">
                                        <CheckCircle2
                                            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                                                plan.featured ? "text-emerald-300" : "text-primary"
                                            }`}
                                        />
                                        <span className={plan.featured ? "text-white/88" : "text-foreground"}>
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                href="#contact"
                                className={`mt-8 inline-flex items-center justify-center gap-2 h-12 rounded-full px-5 font-semibold transition-colors ${
                                    plan.featured
                                        ? "bg-white text-slate-950 hover:bg-white/92"
                                        : "bg-slate-950 text-white hover:bg-slate-800"
                                }`}
                            >
                                {plan.cta}
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
